const STORAGE_PREFIX = 'asaphly-provider-cache-';

export const PROVIDER_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

export const PROVIDER_CACHE_KEYS = {
	permissions: 'permissions',
	teamSongs: 'team-songs',
	setlists: 'setlists',
	team: 'team',
	services: 'services',
	user: 'user',
	version: 'version',
} as const;

export type ProviderCacheKey =
	(typeof PROVIDER_CACHE_KEYS)[keyof typeof PROVIDER_CACHE_KEYS];

export const BOOTSTRAP_PROVIDER_CACHE_KEYS = [
	PROVIDER_CACHE_KEYS.permissions,
	PROVIDER_CACHE_KEYS.teamSongs,
	PROVIDER_CACHE_KEYS.setlists,
	PROVIDER_CACHE_KEYS.team,
] as const;

export const ALL_PROVIDER_CACHE_KEYS = Object.values(PROVIDER_CACHE_KEYS);

type StoredProviderCache<T> = {
	timestamp: number;
	data: T;
};

const getStorageKey = (key: ProviderCacheKey) => `${STORAGE_PREFIX}${key}`;

export const readProviderCache = <T>(key: ProviderCacheKey): T | null => {
	try {
		const raw = window.localStorage.getItem(getStorageKey(key));
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as StoredProviderCache<T>;

		if (
			!parsed?.timestamp ||
			Date.now() - parsed.timestamp > PROVIDER_CACHE_MAX_AGE_MS
		) {
			window.localStorage.removeItem(getStorageKey(key));
			return null;
		}

		return parsed.data ?? null;
	} catch {
		window.localStorage.removeItem(getStorageKey(key));
		return null;
	}
};

export const readProviderCacheUpdatedAt = (
	key: ProviderCacheKey,
): number | undefined => {
	try {
		const raw = window.localStorage.getItem(getStorageKey(key));
		if (!raw) {
			return undefined;
		}

		const parsed = JSON.parse(raw) as StoredProviderCache<unknown>;
		if (
			!parsed?.timestamp ||
			Date.now() - parsed.timestamp > PROVIDER_CACHE_MAX_AGE_MS
		) {
			window.localStorage.removeItem(getStorageKey(key));
			return undefined;
		}

		return parsed.timestamp;
	} catch {
		return undefined;
	}
};

export const writeProviderCache = <T>(key: ProviderCacheKey, data: T) => {
	try {
		const payload: StoredProviderCache<T> = {
			timestamp: Date.now(),
			data,
		};
		window.localStorage.setItem(getStorageKey(key), JSON.stringify(payload));
	} catch (error) {
		console.error(`[provider-cache] Failed to write cache for ${key}`, error);
	}
};

export const removeProviderCache = (key: ProviderCacheKey) => {
	window.localStorage.removeItem(getStorageKey(key));
};

export const hasBootstrapProviderCaches = () =>
	BOOTSTRAP_PROVIDER_CACHE_KEYS.every(
		(key) => readProviderCache(key) !== null,
	);

export const clearProviderCaches = () => {
	ALL_PROVIDER_CACHE_KEYS.forEach((key) => {
		window.localStorage.removeItem(getStorageKey(key));
	});
	window.localStorage.removeItem('asaphly-query-cache');
};
